import { collection, onSnapshot, type Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

/**
 * The event mirror of the platform's invites (D21), as their event admin sees
 * it.
 *
 * The mirror is the same document body the mint command trigger wrote under
 * `platform_invites`, written again under the minting event — which is where
 * an event admin is allowed to read it. The shareable token deliberately is
 * NOT here: the document key is sha256 of the token, precisely so a list of
 * invites is not a list of working credentials, and the token lives only in
 * the mint outcome the minter just received.
 */
export interface EventInvite {
  id: string;
  label: string | undefined;
  maxUses: number;
  issued: number;
  uses: number;
  expiresAt: Timestamp | undefined;
  revokedAt: Timestamp | undefined;
  createdBy: string;
  contextEventSlug: string | undefined;
}

type EventInviteState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; invites: EventInvite[] };

/**
 * Kept live with a listener on the mirror, so a seat taken or a sign-in made
 * while the organiser has the tab open shows at once, and a fresh mint or
 * revoke appears without a reload. The listener stops when the tab unmounts
 * or the event changes.
 */
export function useEventInvites(eventId: string): {
  state: EventInviteState;
} {
  const [state, setState] = useState<EventInviteState>({ status: "loading" });

  useEffect(() => {
    setState({ status: "loading" });
    return onSnapshot(
      collection(db, "events", eventId, "invites"),
      (snapshot) => setInvites(snapshot, setState),
      (err) =>
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load invites",
        }),
    );
  }, [eventId]);

  return { state };
}

type InviteSnapshot = {
  id: string;
  get: (field: string) => unknown;
};

function setInvites(
  snapshot: { docs: InviteSnapshot[] },
  setState: React.Dispatch<React.SetStateAction<EventInviteState>>,
): void {
  setState({
    status: "success",
    // Newest mint first: an organiser distributing today's invite should not
    // scroll last winter's. Ordered client-side, so no index is owed.
    invites: snapshot.docs
      .map((document) => ({
        id: document.id,
        label: document.get("label") as string | undefined,
        maxUses: document.get("maxUses") as number | undefined,
        issued: document.get("issued") as number | undefined,
        uses: document.get("uses") as number | undefined,
        expiresAt: document.get("expiresAt") as Timestamp | undefined,
        revokedAt: (document.get("revokedAt") ?? undefined) as
          Timestamp | undefined,
        createdBy: document.get("createdBy") as string | undefined,
        contextEventSlug: document.get("contextEventSlug") as
          string | undefined,
      }))
      // A malformed field reads as the least it can be — the cap stays the
      // cap, and a count that is missing is nothing, not an error. A row
      // such as this still shows, which is what a listing is for.
      .map((invite) => ({
        ...invite,
        maxUses: invite.maxUses ?? 0,
        issued: invite.issued ?? 0,
        uses: invite.uses ?? 0,
        createdBy: invite.createdBy ?? "",
      }))
      .sort(
        (a, b) =>
          (b.expiresAt?.toMillis() ?? 0) - (a.expiresAt?.toMillis() ?? 0),
      ),
  });
}
